import React, { useEffect, useState, forwardRef, useRef } from 'react';
import MonacoEditor, { loader, OnMount } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import CodeReviewService, { CodeReviewScore } from '../services/CodeReviewService';
import ReactMarkdown from 'react-markdown';
import { judgeService } from '../services/JudgeService';

export type EditorRef = {
  getValue: () => string;
  setValue: (value: string) => void;
  runCode: () => Promise<void>;
};

interface CodeEditorProps {
  code: string;
  language: string;
  onChange: (value: string) => void;
  problemId: string;
  fontSize?: string;
  showLineNumbers?: boolean;
  enableAutoCompletion?: boolean;
  enableSyntaxHighlighting?: boolean;
}

interface ExecutionResult {
  status: string;
  output: string;
  error?: string;
}

// Supported languages mapping
type SupportedLanguageKey = keyof typeof SUPPORTED_LANGUAGES;
const SUPPORTED_LANGUAGES = {
  javascript: { piston: 'javascript', name: 'JavaScript', extension: 'js' },
  python: { piston: 'python3', name: 'Python', extension: 'py' },
  java: { piston: 'java', name: 'Java', extension: 'java' },
  cpp: { piston: 'cpp', name: 'C++', extension: 'cpp' },
  c: { piston: 'c', name: 'C', extension: 'c' }
};

// Tab types for the output panel
type TabType = 'output' | 'feedback';

const CodeEditor = forwardRef<EditorRef, CodeEditorProps>(
  ({ 
    code, 
    language, 
    onChange, 
    problemId,
    fontSize = '14px', 
    showLineNumbers = true, 
    enableAutoCompletion = true, 
    enableSyntaxHighlighting = true
  }, ref) => {
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [executionResult, setExecutionResult] = useState<ExecutionResult | null>(null);
  const [codeReview, setCodeReview] = useState<CodeReviewScore | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [activeTab, setActiveTab] = useState<TabType>('output');
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState('600px');
  const [editorMounted, setEditorMounted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [awaitingInput, setAwaitingInput] = useState(false);
  const [userInputFields, setUserInputFields] = useState<string[]>([]);

  useEffect(() => {
    // Log container dimensions for debugging
    const logDimensions = () => {
      if (containerRef.current) {
        console.log('Container dimensions:', {
          height: containerRef.current.clientHeight,
          width: containerRef.current.clientWidth,
        });
      }
    };

    logDimensions();
    window.addEventListener('resize', logDimensions);

    // Configure Monaco loader
    loader.config({
      paths: {
        vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min/vs'
      }
    });

    return () => window.removeEventListener('resize', logDimensions);
  }, []);

  // Re-layout editor when container dimensions change
  useEffect(() => {
    if (!editorRef.current || !editorMounted) return;

    const observer = new ResizeObserver(() => {
      if (editorRef.current) {
        console.log('Container resized, updating editor layout');
        editorRef.current.layout();
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [editorMounted]);

  const handleEditorDidMount: OnMount = (editor, monacoInstance) => {
    console.log('Editor mounted');
    setIsEditorReady(true);
    editorRef.current = editor;
    setEditorMounted(true);
    
    // Ensure editor is visible and properly sized
    setTimeout(() => {
      editor.layout();
      console.log('Editor layout refreshed');
      
      // Focus editor
      editor.focus();
      console.log('Editor focused');
    }, 100);

    if (ref && 'current' in ref) {
      ref.current = {
        getValue: () => editor.getValue(),
        setValue: (value: string) => editor.setValue(value),
        runCode: async () => {
          await runCode(editor.getValue());
        }
      };
    }
  };

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
  };

  const handleEditorChange = (value: string | undefined) => {
    onChange(value || '');
  };

  // Detect if code likely needs input (scanf, input, etc.)
  function codeNeedsInput(code: string, lang: string) {
    if (lang === 'c' || lang === 'cpp') return /scanf\s*\(/.test(code);
    if (lang === 'python') return /input\s*\(/.test(code);
    if (lang === 'java') return /Scanner|System\.in/.test(code);
    if (lang === 'javascript') return /prompt\s*\(/.test(code);
    return false;
  }

  // Extract prompt messages before input commands (simple static analysis for C, Python, etc.)
  function extractInputPrompts(code: string, lang: string): string[] {
    if (lang === 'c' || lang === 'cpp') {
      // Match printf before scanf
      const lines = code.split(/\r?\n/);
      const prompts: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        if (/scanf\s*\(/.test(lines[i])) {
          for (let j = i - 1; j >= 0; j--) {
            const match = lines[j].match(/printf\s*\(([^;]+)\)/);
            if (match) {
              let msg = match[1];
              msg = msg.replace(/^\s*['\"]|['\"]\s*$/g, '').replace(/\\[nrt]/g, ' ').trim();
              prompts.push(msg);
              break;
            }
            if (!lines[j].trim() || /scanf|return|}/.test(lines[j])) break;
          }
        }
      }
      return prompts;
    } else if (lang === 'python') {
      // Match input() and look for print() before
      const lines = code.split(/\r?\n/);
      const prompts: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        if (/input\s*\(/.test(lines[i])) {
          for (let j = i - 1; j >= 0; j--) {
            const match = lines[j].match(/print\s*\(([^)]+)\)/);
            if (match) {
              let msg = match[1];
              msg = msg.replace(/^\s*['\"]|['\"]\s*$/g, '').replace(/\\[nrt]/g, ' ').trim();
              prompts.push(msg);
              break;
            }
            if (!lines[j].trim() || /input|return|}/.test(lines[j])) break;
          }
        }
      }
      return prompts;
    } else if (lang === 'java') {
      // Match System.out.print before Scanner.next*
      const lines = code.split(/\r?\n/);
      const prompts: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        if (/scanner\.next|scanner\.nextInt|scanner\.nextLine|scanner\.nextDouble|System\.in/.test(lines[i].toLowerCase())) {
          for (let j = i - 1; j >= 0; j--) {
            const match = lines[j].match(/System\.out\.print(?:ln)?\(([^;]+)\)/);
            if (match) {
              let msg = match[1];
              msg = msg.replace(/^\s*['\"]|['\"]\s*$/g, '').replace(/\\[nrt]/g, ' ').trim();
              prompts.push(msg);
              break;
            }
            if (!lines[j].trim() || /scanner|return|}/i.test(lines[j])) break;
          }
        }
      }
      return prompts;
    } else if (lang === 'javascript') {
      // Match prompt() or console.log before prompt()
      const lines = code.split(/\r?\n/);
      const prompts: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        if (/prompt\s*\(/.test(lines[i])) {
          // Look for console.log or string before prompt
          for (let j = i - 1; j >= 0; j--) {
            const match = lines[j].match(/console\.log\(([^;]+)\)/);
            if (match) {
              let msg = match[1];
              msg = msg.replace(/^\s*['\"]|['\"]\s*$/g, '').replace(/\\[nrt]/g, ' ').trim();
              prompts.push(msg);
              break;
            }
            if (!lines[j].trim() || /prompt|return|}/.test(lines[j])) break;
          }
        }
      }
      return prompts;
    }
    return [];
  }

  const runCode = async (code: string) => {
    setExecutionResult(null);
    setCodeReview(null);
    setActiveTab('output');
    setIsRunning(true);
    setAwaitingInput(false);
    setUserInput('');
    // Check if code needs input
    if (codeNeedsInput(code, selectedLanguage)) {
      setAwaitingInput(true);
      setIsRunning(false);
      return;
    }
    try {
      const langKey = selectedLanguage as SupportedLanguageKey;
      const pistonLang = SUPPORTED_LANGUAGES[langKey]?.piston || 'python3';
      const judgeResult = await judgeService.runCode(code, undefined, pistonLang);
      let output = '';
      if (judgeResult.stderr) {
        output = judgeResult.stderr;
      } else if (judgeResult.stdout) {
        output = judgeResult.stdout;
      } else {
        output = judgeResult.output || 'No output.';
      }
      setExecutionResult({
        status: judgeResult.code === 0 ? 'Finished' : 'Error',
        output,
        error: judgeResult.code !== 0 ? output : ''
      });
      const review = await CodeReviewService.getCodeReview(code);
      setCodeReview(review);
    } catch (error: any) {
      setExecutionResult({
        status: 'Error',
        output: '',
        error: error?.message || 'Execution failed.'
      });
    } finally {
      setIsRunning(false);
    }
  };

  const runCodeWithInput = async () => {
    setIsRunning(true);
    setAwaitingInput(false);
    try {
      let input = userInput;
      // If input lines are like 'Enter first number: 10', extract only the numbers
      const prompts = extractInputPrompts(editorRef.current?.getValue() || '', selectedLanguage);
      if (prompts.length > 0) {
        const lines = input.split(/\r?\n/);
        const extracted = lines.map(line => {
          for (const prompt of prompts) {
            if (line.trim().startsWith(prompt)) {
              // Extract value after the prompt
              return line.replace(prompt, '').trim().replace(/^[:\s]+/, '');
            }
          }
          return line.trim();
        });
        input = extracted.join('\n');
      }
      const code = editorRef.current?.getValue() || '';
      const langKey = selectedLanguage as SupportedLanguageKey;
      const pistonLang = SUPPORTED_LANGUAGES[langKey]?.piston || 'python3';
      const judgeResult = await judgeService.runCode(code, input, pistonLang);
      let output = '';
      if (judgeResult.stderr) {
        output = judgeResult.stderr;
      } else if (judgeResult.stdout) {
        output = judgeResult.stdout;
      } else {
        output = judgeResult.output || 'No output.';
      }
      setExecutionResult({
        status: judgeResult.code === 0 ? 'Finished' : 'Error',
        output,
        error: judgeResult.code !== 0 ? output : ''
      });
      const review = await CodeReviewService.getCodeReview(code);
      setCodeReview(review);
    } catch (error: any) {
      setExecutionResult({
        status: 'Error',
        output: '',
        error: error?.message || 'Execution failed.'
      });
    } finally {
      setIsRunning(false);
    }
  };

  const handleSubmit = async () => {
    if (!editorRef.current) return;
    
    setIsSubmitting(true);
    try {
      const code = editorRef.current.getValue();
      await runCode(code);
      
      // Show success message
      setExecutionResult(prev => ({
        ...prev!,
        output: prev!.output + '\n\nSubmission successful! Your code has been submitted for evaluation.'
      }));
    } catch (error) {
      console.error('Error submitting code:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bar graph component for scores
  const ScoreBar = ({ score, label }: { score: number; label: string }) => (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-white/70">{label}</span>
        <span className="text-white/90">{score}%</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full bg-[#2cbb5d] rounded-full" 
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );

  useEffect(() => {
    if (awaitingInput) {
      const prompts = extractInputPrompts(editorRef.current?.getValue() || '', selectedLanguage);
      setUserInputFields(Array(prompts.length).fill(''));
    }
    // eslint-disable-next-line
  }, [awaitingInput, selectedLanguage]);

  function handleUserInputFieldChange(idx: number, value: string) {
    setUserInputFields(fields => {
      const newFields = [...fields];
      newFields[idx] = value;
      return newFields;
    });
  }

  async function runCodeWithInputFields() {
    setIsRunning(true);
    setAwaitingInput(false);
    try {
      const input = userInputFields.join('\n');
      const code = editorRef.current?.getValue() || '';
      const langKey = selectedLanguage as SupportedLanguageKey;
      const pistonLang = SUPPORTED_LANGUAGES[langKey]?.piston || 'python3';
      const judgeResult = await judgeService.runCode(code, input, pistonLang);
      let output = '';
      if (judgeResult.stderr) {
        output = judgeResult.stderr;
      } else if (judgeResult.stdout) {
        output = judgeResult.stdout;
      } else {
        output = judgeResult.output || 'No output.';
      }
      setExecutionResult({
        status: judgeResult.code === 0 ? 'Finished' : 'Error',
        output,
        error: judgeResult.code !== 0 ? output : ''
      });
      const review = await CodeReviewService.getCodeReview(code);
      setCodeReview(review);
    } catch (error: any) {
      setExecutionResult({
        status: 'Error',
        output: '',
        error: error?.message || 'Execution failed.'
      });
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-[#1e1e1e]" ref={containerRef}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#1e1e1e] border-b border-[#444]">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => runCode(editorRef.current?.getValue() || '')}
            disabled={isRunning}
            className="flex items-center px-4 py-1.5 bg-[#2cbb5d] text-white rounded-md hover:bg-[#2cbb5d]/90 transition-colors disabled:opacity-50"
            aria-label="Run code"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z"/>
            </svg>
            {isRunning ? 'Running...' : 'Run'}
          </button>
        </div>
        <select
          value={selectedLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="bg-[#2d2d2d] text-white px-3 py-1.5 rounded-md border border-[#444] focus:outline-none focus:ring-1 focus:ring-white/20"
        >
          {Object.entries(SUPPORTED_LANGUAGES).map(([key, { name }]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Main content area - Vertical layout */}
      <div className="flex flex-col flex-1" style={{ minHeight: 'calc(100vh - 50px)' }}>
        {/* Editor container - Fixed height to ensure visibility */}
        <div style={{ height: '600px', overflow: 'auto' }} className="w-full border border-[#444] rounded-lg shadow-lg overflow-auto">
          {!isEditorReady ? (
            <div className="h-full w-full flex items-center justify-center bg-[#1e1e1e] text-white/50">
              <div className="flex flex-col items-center">
                <svg className="animate-spin h-8 w-8 text-white/30 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Loading Monaco Editor...</span>
              </div>
            </div>
          ) : null}
          <MonacoEditor
            height="600px"
            width="100%"
            language={selectedLanguage.toLowerCase()}
            theme="vs-dark"
            value={code}
            onChange={handleEditorChange}
            onMount={handleEditorDidMount}
            options={{
              fontSize: parseInt(fontSize),
              lineNumbers: showLineNumbers ? 'on' : 'off',
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              quickSuggestions: enableAutoCompletion,
              padding: { top: 10, bottom: 10 },
              renderLineHighlight: 'all',
              cursorStyle: 'line',
              cursorBlinking: 'smooth',
              cursorSmoothCaretAnimation: 'on',
              smoothScrolling: true,
              contextmenu: true,
              mouseWheelZoom: true,
              bracketPairColorization: { enabled: true },
              guides: { bracketPairs: true },
              fontFamily: "'Fira Code', 'Consolas', monospace",
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
                useShadows: false,
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10
              }
            }}
            loading={null}
          />
        </div>

        {/* Divider */}
        <div className="h-1 bg-[#444] hover:bg-[#666] cursor-ns-resize"></div>

        {/* Output panel - Fixed height */}
        <div className="h-[300px] bg-[#181c20] border-t border-[#444] flex flex-col rounded-b-lg shadow-inner">
          {/* Tabs */}
          <div className="flex border-b border-[#444]">
            <button
              onClick={() => setActiveTab('output')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'output' 
                  ? 'text-white border-b-2 border-[#2cbb5d] bg-[#23272b]' 
                  : 'text-white/50 hover:text-white/70'
              } rounded-t-lg transition-colors`}
            >
              Console
            </button>
            <button
              onClick={() => setActiveTab('feedback')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'feedback' 
                  ? 'text-white border-b-2 border-[#2cbb5d] bg-[#23272b]'
                  : 'text-white/50 hover:text-white/70'
              } rounded-t-lg transition-colors`}
              disabled={!codeReview}
            >
              AI Feedback
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-auto p-4">
            {/* Output tab */}
            {activeTab === 'output' && (
              <div className="space-y-2">
                {awaitingInput && (() => {
                  const prompts = extractInputPrompts(editorRef.current?.getValue() || '', selectedLanguage);
                  if (prompts.length > 0) {
                    return (
                      <div className="mb-2 flex flex-col gap-2">
                        <label className="text-white/80 mb-1 text-xs">Your code needs input (e.g. scanf/input). Enter the value for each prompt below, then press Run with Input:</label>
                        {prompts.map((prompt, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-white/70 text-xs w-44">{prompt}</span>
                            <input
                              type="text"
                              className="flex-1 bg-[#232526] border border-[#444] rounded px-2 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2cbb5d]/40 font-mono"
                              value={userInputFields[idx] || ''}
                              onChange={e => handleUserInputFieldChange(idx, e.target.value)}
                              disabled={isRunning}
                            />
                          </div>
                        ))}
                        <button
                          className="mt-2 px-3 py-1.5 bg-[#2cbb5d] text-white rounded hover:bg-[#25994a] transition-colors disabled:opacity-50 w-max self-end"
                          onClick={runCodeWithInputFields}
                          disabled={isRunning || userInputFields.some(v => !v.trim())}
                        >
                          Run with Input
                        </button>
                      </div>
                    );
                  }
                  // fallback: single textarea if no prompts
                  return (
                    <div className="mb-2 flex flex-col">
                      <label className="text-white/80 mb-1 text-xs">Your code needs input (e.g. scanf/input). Enter input below, then press Run with Input:</label>
                      <textarea
                        className="bg-[#232526] border border-[#444] rounded px-2 py-1 text-white text-sm resize-y min-h-[40px] max-h-[120px] focus:outline-none focus:ring-2 focus:ring-[#2cbb5d]/40 font-mono"
                        placeholder="Enter each value on a new line."
                        value={userInput}
                        onChange={e => setUserInput(e.target.value)}
                        disabled={isRunning}
                      />
                      <button
                        className="mt-2 px-3 py-1.5 bg-[#2cbb5d] text-white rounded hover:bg-[#25994a] transition-colors disabled:opacity-50 w-max self-end"
                        onClick={runCodeWithInput}
                        disabled={isRunning || !userInput.trim()}
                      >
                        Run with Input
                      </button>
                    </div>
                  );
                })()}
                {executionResult ? (
                  <div className="font-mono text-sm">
                    {executionResult.error ? (
                      <div className="text-red-400">{executionResult.error}</div>
                    ) : (
                      <div className="text-white/90 whitespace-pre-wrap">{executionResult.output}</div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-white/50">
                    <p>Run your code to see the output</p>
                  </div>
                )}
              </div>
            )}

            {/* Feedback tab */}
            {activeTab === 'feedback' && codeReview && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">Code Review</h3>
                  <div className="text-2xl font-bold text-white">{codeReview.overall}%</div>
                </div>
                
                {/* Summary */}
                <div className="prose prose-invert max-w-none text-white/90">
                  <ReactMarkdown>{codeReview.summary}</ReactMarkdown>
                </div>
                
                {/* Score bars */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <ScoreBar score={codeReview.correctness} label="Correctness" />
                  <ScoreBar score={codeReview.readability} label="Readability" />
                  <ScoreBar score={codeReview.efficiency} label="Efficiency" />
                  <ScoreBar score={codeReview.bestPractices} label="Best Practices" />
                  <ScoreBar score={codeReview.maintainability} label="Maintainability" />
                  <ScoreBar score={codeReview.documentation} label="Documentation" />
                  <ScoreBar score={codeReview.security} label="Security" />
                </div>
                
                {/* Recommendations */}
                {codeReview.recommendations.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-white font-medium mb-2">Recommendations:</h4>
                    <ul className="list-disc list-inside space-y-1 text-white/70">
                      {codeReview.recommendations.map((recommendation, index) => (
                        <li key={index}>{recommendation}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CodeEditor;
